import { useEffect, useMemo, useState } from 'react';
import type { IslandProps } from '../registry';

/**
 * ProgramList island — the `full` (searchable) program page.
 *
 * Mounts into: <div data-react="program" data-mode="full" data-edition="6"></div>
 * (rendered by node--program.html.twig for programs whose field_mode = `full`).
 *
 * Fetches events same-origin via JSON:API — no CORS. Events are scoped to the
 * program's edition OR all-editions (empty field_edition), matching the daily
 * page's edition rule. A text search + event-kind + day filter narrow the list
 * client-side; each event is collapsed (date + title) and expands to its short
 * description, full date/time, artist and ticket price (empty/0 → "free").
 *
 * The site runs in UTC (system.date:timezone.default = UTC), so the ISO values
 * JSON:API returns are already the display wall-clock — we format them in UTC to
 * match the server-rendered daily page.
 */

type EventItem = {
  id: string;
  title: string;
  url: string;
  artist: string;
  short: string;
  price: number | null;
  start: Date | null;
  end: Date | null;
  kindId: string | null;
  kindLabel: string;
};

type Strings = {
  search: string;
  searchPh: string;
  allKinds: string;
  kind: string;
  dates: string;
  from: string;
  to: string;
  reset: string;
  loading: string;
  error: string;
  empty: string;
  count: (n: number) => string;
  free: string;
  artist: string;
  price: string;
  more: string;
};

const I18N: Record<string, Strings> = {
  sl: {
    search: 'Iskanje',
    searchPh: 'Išči po naslovu, umetniku…',
    allKinds: 'Vse zvrsti',
    kind: 'Zvrst',
    dates: 'Obdobje',
    from: 'Od',
    to: 'Do',
    reset: 'Ponastavi',
    loading: 'Nalaganje…',
    error: 'Programa trenutno ni mogoče naložiti.',
    empty: 'Ni dogodkov, ki bi ustrezali iskanju.',
    count: (n) => (n === 1 ? '1 dogodek' : `${n} dogodkov`),
    free: 'Brezplačno',
    artist: 'Nastopajoči',
    price: 'Cena',
    more: 'Več o dogodku',
  },
  en: {
    search: 'Search',
    searchPh: 'Search by title, artist…',
    allKinds: 'All kinds',
    kind: 'Kind',
    dates: 'Period',
    from: 'From',
    to: 'To',
    reset: 'Reset',
    loading: 'Loading…',
    error: 'The programme could not be loaded right now.',
    empty: 'No events match your search.',
    count: (n) => (n === 1 ? '1 event' : `${n} events`),
    free: 'Free entry',
    artist: 'Performer',
    price: 'Price',
    more: 'More about the event',
  },
};

/** YYYY-MM-DD key of a Date in UTC (site tz). */
function dayKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function ProgramList({ dataset }: IslandProps) {
  const lang = (typeof document !== 'undefined' && document.documentElement.lang) || 'sl';
  const t = I18N[lang] ?? I18N.sl;
  const edition = dataset.edition ?? '';

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'sl-SI', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    [lang],
  );
  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'sl-SI', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
      }),
    [lang],
  );
  const [events, setEvents] = useState<EventItem[]>([]);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  // Optional date window (YYYY-MM-DD from <input type="date">); either may be ''.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');

    const p = new URLSearchParams();
    p.set('filter[status][value]', '1');
    p.set('include', 'field_event_kind');
    p.set('sort', 'field_when.value');
    p.set('fields[node--event]', 'title,path,field_short_desc,field_artist,field_ticket_price,field_when,field_event_kind');
    p.set('fields[taxonomy_term--event_kind]', 'name');
    p.set('page[limit]', '50');
    // Edition scope: this edition OR all-editions (empty field_edition).
    if (edition) {
      p.set('filter[ed][group][conjunction]', 'OR');
      p.set('filter[has][condition][path]', 'field_edition.drupal_internal__tid');
      p.set('filter[has][condition][value]', edition);
      p.set('filter[has][condition][memberOf]', 'ed');
      p.set('filter[no][condition][path]', 'field_edition');
      p.set('filter[no][condition][operator]', 'IS NULL');
      p.set('filter[no][condition][memberOf]', 'ed');
    }

    fetch(`/${lang}/jsonapi/node/event?${p.toString()}`, {
      signal: controller.signal,
      headers: { Accept: 'application/vnd.api+json' },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((json) => {
        const kinds = new Map<string, string>();
        for (const inc of (json?.included ?? []) as Array<{
          type: string;
          id: string;
          attributes?: { name?: string };
        }>) {
          if (inc.type === 'taxonomy_term--event_kind') {
            kinds.set(inc.id, inc.attributes?.name ?? '');
          }
        }

        const data = (json?.data ?? []) as Array<{
          id: string;
          attributes?: Record<string, any>;
          relationships?: Record<string, any>;
        }>;

        const parsed: EventItem[] = data.map((d) => {
          const a = d.attributes ?? {};
          const when = a.field_when ?? {};
          const kindRef = d.relationships?.field_event_kind?.data;
          const priceRaw = a.field_ticket_price;
          const price = priceRaw == null || priceRaw === '' ? null : parseFloat(priceRaw);
          return {
            id: d.id,
            title: a.title ?? '',
            url: a.path?.alias ?? '#',
            artist: a.field_artist ?? '',
            short: a.field_short_desc ?? '',
            price,
            start: when.value ? new Date(when.value) : null,
            end: when.end_value ? new Date(when.end_value) : null,
            kindId: kindRef?.id ?? null,
            kindLabel: kindRef?.id ? kinds.get(kindRef.id) ?? '' : '',
          };
        });

        setEvents(parsed);
        setState('idle');
      })
      .catch((e) => {
        if (e?.name !== 'AbortError') setState('error');
      });

    return () => controller.abort();
  }, [edition, lang]);

  // Distinct event kinds present, for the filter dropdown.
  const kindOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of events) {
      if (e.kindId && !seen.has(e.kindId)) seen.set(e.kindId, e.kindLabel);
    }
    return [...seen.entries()];
  }, [events]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (kind && e.kindId !== kind) return false;
      // Date window: keep events whose [start..end] overlaps [from..to].
      if (from || to) {
        if (!e.start) return false;
        const startDay = dayKey(e.start);
        const endDay = dayKey(e.end ?? e.start);
        if (from && endDay < from) return false;
        if (to && startDay > to) return false;
      }
      if (q) {
        const hay = `${e.title} ${e.artist} ${e.short} ${e.kindLabel}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [events, query, kind, from, to]);

  const whenLabel = (e: EventItem): string => {
    if (!e.start) return '';
    const sDay = dayKey(e.start);
    const eDay = e.end ? dayKey(e.end) : sDay;
    if (sDay === eDay) {
      const st = timeFmt.format(e.start);
      const et = e.end ? timeFmt.format(e.end) : st;
      const time = st === et ? st : `${st}–${et}`;
      return `${dateFmt.format(e.start)}, ${time}`;
    }
    return `${dateFmt.format(e.start)} – ${dateFmt.format(e.end as Date)}`;
  };

  const priceLabel = (e: EventItem): string =>
    e.price == null || e.price === 0 ? t.free : `${e.price} €`;

  const hasFilters = query !== '' || kind !== '' || from !== '' || to !== '';

  if (state === 'loading') return <p className="reciklart-program__status">{t.loading}</p>;
  if (state === 'error') return <p className="reciklart-program__status">{t.error}</p>;

  return (
    <div className="reciklart-program__full">
      <div className="reciklart-program__controls">
        <label className="reciklart-program__field reciklart-program__field--search">
          <span className="reciklart-program__field-label">{t.search}</span>
          <input
            type="search"
            value={query}
            placeholder={t.searchPh}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>

        {kindOptions.length > 0 && (
          <label className="reciklart-program__field">
            <span className="reciklart-program__field-label">{t.kind}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">{t.allKinds}</option>
              {kindOptions.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="reciklart-program__field">
          <span className="reciklart-program__field-label">{t.dates}</span>
          <div className="reciklart-program__dates">
            <input
              type="date"
              aria-label={t.from}
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
            <input
              type="date"
              aria-label={t.to}
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        {hasFilters && (
          <button
            type="button"
            className="reciklart-program__reset"
            onClick={() => {
              setQuery('');
              setKind('');
              setFrom('');
              setTo('');
            }}
          >
            {t.reset}
          </button>
        )}
      </div>

      <p className="reciklart-program__count" aria-live="polite">
        {t.count(filtered.length)}
      </p>

      {filtered.length === 0 ? (
        <p className="reciklart-program__empty">{t.empty}</p>
      ) : (
        <div className="reciklart-program__events">
          {filtered.map((e) => (
            <details className="reciklart-program__event" key={e.id}>
              <summary className="reciklart-program__event-head">
                <span className="reciklart-program__event-meta">
                  {whenLabel(e)}
                  {e.kindLabel ? ` · ${e.kindLabel}` : ''}
                </span>
                <span className="reciklart-program__event-title">{e.title}</span>
              </summary>
              <div className="reciklart-program__event-detail">
                {e.short && <p>{e.short}</p>}
                <dl className="reciklart-program__facts">
                  {e.artist && (
                    <>
                      <dt>{t.artist}</dt>
                      <dd>{e.artist}</dd>
                    </>
                  )}
                  <dt>{t.price}</dt>
                  <dd>{priceLabel(e)}</dd>
                </dl>
                <a className="reciklart-program__link" href={e.url}>
                  {t.more}
                </a>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
